import React, { useCallback, useEffect } from "react";
import Post from "./Post";
import { makeStyles, Button, Box } from "@material-ui/core";
import api from "../service/api";

export default function PostagensTabs({
  tabName,
  apiEndpoint,
  tabState,
  onUpdateTabState,
  onSetTabLoading,
  onSetTabPosts,
}) {
  const classes = useStyles();
  const {
    posts,
    hasNext,
    hasPrevFlag,
    limit,
    offset,
    cursor,
    nextCursor,
    prevCursors,
    loading,
    initialized
  } = tabState;

  const updateData = useCallback((response) => {
    const root = response?.data?.data ?? response?.data;
    let list = [];
    if (Array.isArray(root)) list = root;
    else if (Array.isArray(root?.data)) list = root.data;
    else if (Array.isArray(root?.items)) list = root.items;
    else if (Array.isArray(response?.data)) list = response.data;

    const nextCur = root?.nextCursor ?? root?.pagination?.nextCursor ?? null;
    const nextFlag = root?.hasNext ?? root?.pagination?.hasNext ?? (nextCur ? true : false);
    const prevFlag = root?.hasPrev ?? root?.pagination?.hasPrev ?? false;

    onSetTabPosts(tabName, list || [], {
      hasNext: !!nextFlag,
      hasPrevFlag: !!prevFlag,
      nextCursor: nextCur || null
    });
  }, [tabName, onSetTabPosts]);

  const getPosts = useCallback(
    async (opts = {}) => {
      onSetTabLoading(tabName, true);

      const c = opts.hasOwnProperty("cursor") ? opts.cursor : cursor;
      const off = opts.hasOwnProperty("offset") ? Math.max(0, opts.offset) : offset;

      let url = `${apiEndpoint}?limit=${limit}`;
      if (c !== null && c !== undefined) {
        url += `&cursor=${encodeURIComponent(c)}`;
      } else if (off) {
        url += `&offset=${off}`;
      }

      try {
        const response = await api.get(url);
        updateData(response);

        // Atualizar cursor e offset no estado da aba
        onUpdateTabState(tabName, {
          cursor: c ?? null,
          offset: c === null || c === undefined ? off : offset
        });
      } catch (e) {
        console.error(`Erro ao carregar posts do ${tabName}:`, e);
      } finally {
        onSetTabLoading(tabName, false);
      }
    },
    [tabName, apiEndpoint, limit, cursor, offset, updateData, onSetTabLoading, onUpdateTabState]
  );

  useEffect(() => {
    if (!initialized) {
      getPosts({ cursor: null, offset: 0 });
    }
  }, [initialized, getPosts]);

  const handleNext = () => {
    if (!hasNext || loading) return;
    if (nextCursor) {
      // Adicionar cursor atual ao histórico
      onUpdateTabState(tabName, {
        prevCursors: [...prevCursors, cursor]
      });
      getPosts({ cursor: nextCursor });
    } else {
      const nextOff = offset + limit;
      getPosts({ offset: nextOff });
    }
  };

  const handlePrev = () => {
    const canPrev = prevCursors.length > 0 || hasPrevFlag;
    if (!canPrev || loading) return;

    if (prevCursors.length > 0) {
      const newPrevCursors = [...prevCursors];
      const prev = newPrevCursors.pop();

      onUpdateTabState(tabName, {
        prevCursors: newPrevCursors
      });

      getPosts({ cursor: prev ?? null, offset: prev == null ? 0 : undefined });
    } else {
      const prevOff = Math.max(0, offset - limit);
      getPosts({ offset: prevOff });
    }
  };

  return (
    <div className={classes.root}>
      {!loading ? (
        <>
          {posts?.map((post, idx) => {
            const key = post?.id || post?.igId || `${post?.type || 'item'}_${post?.createdTime || post?.timestamp || idx}`;
            return <Post key={key} post={post} source={tabName} />;
          })}
          {posts?.length === 0 && (
            <div className={classes.emptyMessage}>
              Nenhuma postagem encontrada
            </div>
          )}
          <Box className={classes.pager}>
            <Button
              onClick={handlePrev}
              disabled={!(prevCursors.length > 0 || hasPrevFlag) || loading}
              variant="outlined"
            >
              Anterior
            </Button>
            <Button
              onClick={handleNext}
              disabled={!hasNext || loading}
              color="primary"
              variant="contained"
            >
              Próximo
            </Button>
          </Box>
        </>
      ) : (
        <div className={classes.loading}>
          <span>Carregando...</span>
        </div>
      )}
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    paddingBottom: "46px",
    paddingTop: "19px",
    gap: "50px",
    flexDirection: "column",
    alignItems: "center",
    display: "flex",
  },
  pager: {
    display: "flex",
    gap: 12,
  },
  loading: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.colors?.gray1 || theme.palette.text.secondary,
    fontSize: 18,
    fontFamily: 'Karla, sans-serif',
  },
  emptyMessage: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.colors?.gray1 || theme.palette.text.secondary,
    fontSize: 18,
    fontFamily: 'Karla, sans-serif',
  }
}));